'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import { useTranslation } from 'react-i18next';

export default function IngredientsSection() {
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const ingredients = [
    {
      name: t('ingredients.cards.neem.name'),
      emoji: '🌿',
      tagline: t('ingredients.cards.neem.tagline'),
      description: t('ingredients.cards.neem.description'),
      benefit: t('ingredients.cards.neem.benefits'),
      image: '/assets/images/ingredient-neem.png',
      imageAlt: t('ingredients.cards.neem.imageAlt'),
      color: 'from-primary-50 to-surface',
      accentColor: 'bg-primary text-white',
      number: '01'
    },
    {
      name: t('ingredients.cards.tulsi.name'),
      emoji: '🌱',
      tagline: t('ingredients.cards.tulsi.tagline'),
      description: t('ingredients.cards.tulsi.description'),
      benefit: t('ingredients.cards.tulsi.benefits'),
      image: '/assets/images/ingredient-tulsi.png',
      imageAlt: t('ingredients.cards.tulsi.imageAlt'),
      color: 'from-blue-pale to-surface',
      accentColor: 'bg-blue-brand text-white',
      number: '02'
    },
    {
      name: t('ingredients.cards.lemon.name'),
      emoji: '🍋',
      tagline: t('ingredients.cards.lemon.tagline'),
      description: t('ingredients.cards.lemon.description'),
      benefit: t('ingredients.cards.lemon.benefits'),
      image: '/assets/images/ingredient-lemon.png',
      imageAlt: t('ingredients.cards.lemon.imageAlt'),
      color: 'from-amber-50 to-surface',
      accentColor: 'bg-gold text-foreground',
      number: '03'
    }
  ];

  return (
    <section
      id="ingredients"
      ref={ref}
      // FIX 1: Reduced mobile padding & swapped to overflow-x-clip for safety
      className="py-16 lg:py-16 bg-surface overflow-x-clip"
      aria-labelledby="ingredients-heading">
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          // FIX 2: Tighter bottom margin on mobile (mb-12 vs mb-20)
          className="mb-12 lg:mb-20 "
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}>
          
          <span className="text-xs font-semibold tracking-widest uppercase text-muted mb-3 lg:mb-4 block">
            {t('ingredients.label')}
          </span>
          <h2
            id="ingredients-heading"
            // FIX 3: Scaled responsive typography for the heading
            className="section-headline text-3xl sm:text-4xl lg:text-5xl font-display font-light text-foreground leading-tight">
            
            {t('ingredients.title.line1')}
            <br />
            <span className="italic pl-0.5 pr-2 text-gradient-green">{t('ingredients.title.highlight')}</span> {t('ingredients.title.line2')}
          </h2>
        </motion.div>

        {/* Ingredient cards — alternating layout */}
        {/* FIX 4: Reduced vertical gap between cards on mobile */}
        <div className="space-y-8 lg:space-y-20">
          {ingredients?.map((ingredient, i) =>
            <motion.article
              key={ingredient?.name}
              // FIX 5: Adjusted padding for mobile (p-6) up to desktop (lg:p-14)
              className={`ingredient-card grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-16 items-center bg-gradient-to-br ${ingredient?.color} rounded-4xl lg:rounded-5xl p-6 sm:p-8 lg:p-14 border border-border overflow-hidden relative`}
              initial={{ opacity: 0, y: 60 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 1,
                delay: 0.2 + i * 0.2,
                ease: [0.16, 1, 0.3, 1]
              }}
              whileHover={{ y: -6 }}
              aria-label={`${ingredient?.name} — natural ingredient in Dr. Detoxer`}>
              
              {/* Background number */}
              <div
                // FIX 6: Removed hardcoded `12rem` inline style. Used Tailwind responsive text sizes.
                  className={`absolute top-2 lg:top-6 font-display font-light text-[8rem] lg:text-[12rem] opacity-[0.06] select-none pointer-events-none leading-none z-0
                  ${i % 2 === 1 
                    ? 'left-4 lg:left-8' 
                    : 'right-4 lg:right-8'}
                `}
                aria-hidden="true"
              >
                {ingredient?.number}
              </div>

              {/* Image — alternates sides on md+ screens, always on top for mobile */}
              <div
                className={`col-span-1 md:col-span-5 order-1 ${i % 2 === 1 ? 'md:order-2' : 'md:order-1'}`}>
                
                {/* FIX 7: Switched to aspect-video on mobile to save vertical scrolling, aspect-[4/3] on md+ */}
                <div className="relative rounded-3xl lg:rounded-4xl overflow-hidden shadow-medium aspect-video md:aspect-[4/3] w-full">
                  <AppImage
                    src={ingredient?.image}
                    alt={ingredient?.imageAlt}
                    width={700}
                    height={525}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  
                  <div className="absolute top-3 left-3 lg:top-4 lg:left-4 z-10">
                    <span
                      className={`${ingredient?.accentColor} px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-semibold tracking-wide shadow-sm`}>
                      {ingredient?.number}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div
                className={`col-span-1 md:col-span-7 order-2 ${i % 2 === 1 ? 'md:order-1' : 'md:order-2'} relative z-10`}>
                
                <span className="text-3xl lg:text-4xl mb-3 lg:mb-4 block">{ingredient?.emoji}</span>
                <p className="text-muted text-[10px] lg:text-xs tracking-widest uppercase font-semibold mb-2">
                  {ingredient?.tagline}
                </p>
                {/* FIX 8: Scaled down the title font for mobile */}
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-foreground mb-4 lg:mb-6">
                  {ingredient?.name}
                </h3>
                <p className="text-muted text-sm lg:text-base leading-relaxed font-light mb-6">
                  {ingredient?.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {ingredient?.benefit?.split(' · ')?.map((b) =>
                    <span
                      key={b}
                      className="bg-white/80 text-foreground text-[10px] lg:text-xs px-3 py-1.5 rounded-full border border-border font-medium shadow-sm backdrop-blur-sm">
                      {b}
                    </span>
                  )}
                </div>
              </div>
            </motion.article>
          )}
        </div>
      </div>
    </section>
  );
}